import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '@consta/uikit/Card';
import { Text } from '@consta/uikit/Text';
import { Button } from '@consta/uikit/Button';
import { Combobox } from '@consta/uikit/Combobox';
import { Table, TableColumn, TableFilters, TableTextFilter } from '@consta/uikit/Table';
import { TextField } from '@consta/uikit/TextField';
import { IconEdit } from '@consta/icons/IconEdit';
import { IconSearchStroked } from '@consta/icons/IconSearchStroked';
import { useDebounce } from '@consta/uikit/useDebounce';
import classes from './WellsContent.module.scss';
import { useLazyGetWellQuery,  usePostWellExportMutation } from '../../../services/wellService';
import { useLazyGetFieldQuery } from '../../../services/fieldService';
import { useLazyGetProjectQuery } from '../../../services/projectService';
import { Pagination } from '@consta/uikit/Pagination/index';

interface RowWells {
    id: string;
    number: string;
    workshop: string;
    team: string;
    horizons_info: string;
    horizons_info_code: string;
    layers_info: string;
    liquid_rate: string;
    water_cut: string;
    category_name: string;
    areas: string;
    oil_rate: string;
    water_rate: string;
    x_coords: string;
    y_coords: string;
    studied_phase: string;
    research_objective: string;
    note: string;
    is_sample_taken: string;
    sampling_date: string;
    sample_note: string;
    oil_volume: string;
    is_oil_split: string;
    is_water_sample_received: string;
    water_volume: string;
    is_water_split: string;
    water_density: string;
    water_ec: string;
    is_gh: string;
    is_icp: string;
    is_hav: string;
    is_kapel: string;
    action: any;
}

interface ProjectItem {
    id: string;
    label: string;
}

interface FieldItem {
    id: string;
    label: string;
}

const WellsContent: React.FC = () => {
    const [selectedProject, setSelectedProject] = useState<ProjectItem | null>(null);
    const [selectedField, setSelectedField] = useState<FieldItem | null>(null);
    const [projects, setProjects] = useState<ProjectItem[]>([]);
    const [fields, setFields] = useState<FieldItem[]>([]);
    const [searchField, setSearchField] = useState<string | null>('');
    const [filter, setFilter] = useState<any>({});
    const [ordering, setOrdering] = useState<any>();
    const [currentPage, setCurrentPage] = useState(1); // Текущая страница
    const [searchParam, setSearchParam] = useState<string>('');
    
    const [getWells, { data: wellsData, isLoading: isLoadingWells, isFetching }] = useLazyGetWellQuery();
    const [getFields] = useLazyGetFieldQuery();
    const [getProjects] = useLazyGetProjectQuery();
    const [postExportWell, { isLoading: isExporting }] = usePostWellExportMutation();

    const dateStart = new Date();
    const yearStart = dateStart.getFullYear();
    const monthStart = (dateStart.getMonth() + 1).toString().padStart(2, '0');
    const dayStart = dateStart.getDate().toString().padStart(2, '0');
    const formattedDateStart = `${yearStart}-${monthStart}-${dayStart}`;

    const itemsPerPage = 20; // Изменено с 9999 на 20

    // Подготовка параметров
    const wellsParams = {
        limit: itemsPerPage,
        offset: itemsPerPage * (currentPage - 1), // Исправлено вычисление offset
        search: searchParam || undefined,
        ...Object.entries(filter ?? {}).reduce((acc: any, [key, value]: any) => {
            if (value && value.length > 0) {
                if (key.startsWith('is_')) {
                    acc[key] = value.map((val: string) => val === 'Да');
                } else {
                    acc[key] = Array.isArray(value) ? value.join(',') : value;
                }
            }
            return acc;
        }, {}),
    };

    // Добавляем проекты и месторождения в параметры
    if (selectedProject && selectedProject.id !== 'all') {
        wellsParams.projects = [selectedProject.id];
    }
    
    if (selectedField && selectedField.id !== 'all') {
        wellsParams.fields = [selectedField.id];
    }

    // Запрос при изменении параметров
    useEffect(() => {
        getWells(wellsParams);
    }, [ordering, currentPage, searchParam, filter, selectedProject, selectedField]);

    const handleExportToExcel = async () => {
        try {
            const exportParams = {
                ...wellsParams,
                limit: undefined,
                offset: undefined,
                date: formattedDateStart
            };

            const response = await postExportWell(exportParams).unwrap();
            
            if (response instanceof Blob) {
                downloadFile(response, `Сводный_отчет_по_скважинам_${formattedDateStart}.xlsx`);
            } else {
                alert('Экспорт завершен успешно!');
            }
        } catch (error) {
            console.error('Ошибка при экспорте в Excel:', error);
            alert('Произошла ошибка при экспорте данных');
        }
    }

    const downloadFile = (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    // Debounce для поиска
    const debounceSetSearchValue = useDebounce((value) => {
        setSearchParam(value ?? '');
        setCurrentPage(1); // Сброс на первую страницу при поиске
    }, 500);

    useEffect(() => {
        debounceSetSearchValue(searchField);
    }, [searchField]);

    // Загрузка проектов и месторождений при монтировании
    useEffect(() => {
        loadProjects();
        loadFields();
    }, []);

    const loadProjects = async () => {
        try {
            const response = await getProjects({
                limit: 100,
                offset: 0,
                ordering: 'name'
            }).unwrap();
            
            const projectItems: ProjectItem[] = [
                { id: 'all', label: 'Все проекты' },
                ...response.results.map((project: any) => ({
                    id: project.uuid,
                    label: project.name
                }))
            ];
            
            setProjects(projectItems);
            setSelectedProject(projectItems[0]);
        } catch (error) {
            console.error('Ошибка загрузки проектов:', error);
        }
    };

    const loadFields = async () => {
        try {
            const response = await getFields({
                limit: 100,
                offset: 0,
                ordering: 'name'
            }).unwrap();
            
            const fieldItems: FieldItem[] = [
                { id: 'all', label: 'Все месторождения' },
                ...response.results.map(field => ({
                    id: field.uuid,
                    label: field.name
                }))
            ];
            
            setFields(fieldItems);
            setSelectedField(fieldItems[0]);
        } catch (error) {
            console.error('Ошибка загрузки месторождений:', error);
        }
    };

    

    const transformWellData = (well: any): RowWells => {
        const horizonsText = well.horizons_info && Array.isArray(well.horizons_info) 
            ? well.horizons_info.map((h: any) => h.name).join(', ')
            : '-';
        const horizonsCodeText = well.horizons_info && Array.isArray(well.horizons_info) && well.horizons_info?.filter((item) => item.code)?.length > 1
            ? well.horizons_info.map((h: any) => h.code).join(', ')
            : '-';

        const layersText = well.layers_info && Array.isArray(well.layers_info) 
            ? well.layers_info.map((l: any) => l.name).join(', ')
            : '-';

        return {
            id: well.uuid || well.id?.toString() || '-',
            number: well.number?.toString() || '-',
            workshop: well.workshop?.toString() || '-',
            team: well.team?.toString() || '-',
            horizons_info: horizonsText,
            horizons_info_code: horizonsCodeText,
            layers_info: layersText,
            liquid_rate: well.liquid_rate?.toString() || '-',
            water_cut: well.water_cut?.toString() || '-',
            category_name: well.category_name || '-',
            areas: well.area_name || '-',
            oil_rate: well.oil_rate?.toString() || '-',
            water_rate: well.water_rate?.toString() || '-',
            x_coords: well.x_coords?.toString() || '-',
            y_coords: well.y_coords?.toString() || '-',
            studied_phase: well.studied_phase || '-',
            research_objective: well.research_objective || '-',
            note: well.note || '-',
            is_sample_taken: well.is_sample_taken ? 'Да' : 'Нет',
            sampling_date: well.sampling_date || '-',
            sample_note: well.sample_note || '-',
            oil_volume: well.oil_volume?.toString() || '-',
            is_oil_split: well.is_oil_split ? 'Да' : 'Нет',
            is_water_sample_received: well.is_water_sample_received ? 'Да' : 'Нет',
            water_volume: well.water_volume?.toString() || '-',
            is_water_split: well.is_water_split ? 'Да' : 'Нет',
            water_density: well.water_density?.toString() || '-',
            water_ec: well.water_ec?.toString() || '-',
            is_gh: well.is_gh ? 'Да' : 'Нет',
            is_icp: well.is_icp ? 'Да' : 'Нет',
            is_hav: well.is_hav ? 'Да' : 'Нет',
            is_kapel: well.is_kapel ? 'Да' : 'Нет',
            action: (
                <Button
                    iconLeft={IconEdit}
                    size='xs'
                    view='clear'
                    disabled={!well.is_editable}
                />
            )
        };
    };

    const allRows: RowWells[] = wellsData?.results
        ? wellsData.results.map(transformWellData)
        : [];

    const totalCount = wellsData?.count || 0;
    const totalPages = Math.ceil(totalCount / itemsPerPage);

    // Получение уникальных значений для фильтров
    const getUniqueValues = (field: keyof RowWells) => {
        const uniqueValues = allRows
            .map(row => row[field])
            .filter((value, index, self) => 
                value && value !== '-' && self.indexOf(value) === index
            )
            .map(value => ({
                name: value.toString(),
                value: value.toString(),
            }));

        return uniqueValues;
    };

    // Исправленная функция updateTable
    const updateTable = (filters: any) => {
        const newFilters: { [key: string]: any } = {};
        
        Object.entries(filters).forEach(([filterId, filterData]: [string, any]) => {
            if (filterData && filterData.value && filterData.value.length > 0) {
                const filterValues = filterData.value.map((item: any) => {
                    const value = typeof item === 'object' && item !== null ? item.value : item;
                    return value;
                });
                newFilters[filterId] = filterValues;
            }
        });

        setFilter(newFilters);
        setCurrentPage(1); // Сброс на первую страницу при изменении фильтров
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    // Фильтры для таблицы
    //@ts-ignore
    const tableFilters: TableFilters<RowWells> = [
        {
            id: 'number',
            name: '№ скважины',
            field: 'number',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    withSearch: true,
                    items: getUniqueValues('number'),
                },
            },
        },
        {
            id: 'workshop',
            name: 'Цех',
            field: 'workshop',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    withSearch: true,
                    items: getUniqueValues('workshop'),
                },
            },
        },
        {
            id: 'team',
            name: 'Бригада',
            field: 'team',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    withSearch: true,
                    items: getUniqueValues('team'),
                },
            },
        },
        {
            id: 'horizons_info',
            name: 'Название горизонта',
            field: 'horizons_info',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    withSearch: true,
                    items: getUniqueValues('horizons_info'),
                },
            },
        },
        // {
        //     id: 'horizons_info_code',
        //     name: 'Шифр горизонта',
        //     field: 'horizons_info_code',
        //     filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
        //         return filterValues.some(
        //             (filterValue) => filterValue && filterValue.value === cellValue,
        //         );
        //     },
        //     component: {
        //         name: TableTextFilter,
        //         props: {
        //             withSearch: true,
        //             items: getUniqueValues('horizons_info_code'),
        //         },
        //     },
        // },
        {
            id: 'layers_info',
            name: 'Пласт',
            field: 'layers_info',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    withSearch: true,
                    items: getUniqueValues('layers_info'),
                },
            },
        },
        {
            id: 'liquid_rate',
            name: 'Дебит скважины по жидкости',
            field: 'liquid_rate',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    withSearch: true,
                    items: getUniqueValues('liquid_rate'),
                },
            },
        },
        {
            id: 'water_cut',
            name: 'Обводнённость (%)',
            field: 'water_cut',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    withSearch: true,
                    items: getUniqueValues('water_cut'),
                },
            },
        },
        {
            id: 'category_name',
            name: 'Категория',
            field: 'category_name',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    withSearch: true,
                    items: getUniqueValues('category_name'),
                },
            },
        },
        {
            id: 'areas',
            name: 'Участок',
            field: 'areas',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    withSearch: true,
                    items: getUniqueValues('areas'),
                },
            },
        },
        {
            id: 'oil_rate',
            name: 'Q нефти',
            field: 'oil_rate',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    withSearch: true,
                    items: getUniqueValues('oil_rate'),
                },
            },
        },
        {
            id: 'water_rate',
            name: 'Q жидкости',
            field: 'water_rate',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    withSearch: true,
                    items: getUniqueValues('water_rate'),
                },
            },
        },
        {
            id: 'x_coords',
            name: 'Координаты X',
            field: 'x_coords',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    withSearch: true,
                    items: getUniqueValues('x_coords'),
                },
            },
        },
        {
            id: 'y_coords',
            name: 'Координаты Y',
            field: 'y_coords',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    withSearch: true,
                    items: getUniqueValues('y_coords'),
                },
            },
        },
        {
            id: 'studied_phase',
            name: 'Исследуемая фаза',
            field: 'studied_phase',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    withSearch: true,
                    items: getUniqueValues('studied_phase'),
                },
            },
        },
        {
            id: 'research_objective',
            name: 'Цель исследования',
            field: 'research_objective',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    withSearch: true,
                    items: getUniqueValues('research_objective'),
                },
            },
        },
        {
            id: 'is_sample_taken',
            name: 'Проба отобрана',
            field: 'is_sample_taken',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    items: [
                        { name: 'Да', value: 'Да' },
                        { name: 'Нет', value: 'Нет' }
                    ],
                },
            },
        },
        {
            id: 'sampling_date',
            name: 'Дата отбора пробы',
            field: 'sampling_date',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    withSearch: true,
                    items: getUniqueValues('sampling_date'),
                },
            },
        },
        {
            id: 'oil_volume',
            name: 'Объем нефти',
            field: 'oil_volume',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    withSearch: true,
                    items: getUniqueValues('oil_volume'),
                },
            },
        },
        {
            id: 'is_oil_split',
            name: 'Разлито нефть',
            field: 'is_oil_split',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    items: [
                        { name: 'Да', value: 'Да' },
                        { name: 'Нет', value: 'Нет' }
                    ],
                },
            },
        },
        {
            id: 'is_water_sample_received',
            name: 'Получен образец воды',
            field: 'is_water_sample_received',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    items: [
                        { name: 'Да', value: 'Да' },
                        { name: 'Нет', value: 'Нет' }
                    ],
                },
            },
        },
        {
            id: 'water_volume',
            name: 'Объем воды',
            field: 'water_volume',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    withSearch: true,
                    items: getUniqueValues('water_volume'),
                },
            },
        },
        {
            id: 'is_water_split',
            name: 'Отфильтровано вода',
            field: 'is_water_split',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    items: [
                        { name: 'Да', value: 'Да' },
                        { name: 'Нет', value: 'Нет' }
                    ],
                },
            },
        },
        {
            id: 'water_density',
            name: 'Плотность',
            field: 'water_density',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    withSearch: true,
                    items: getUniqueValues('water_density'),
                },
            },
        },
        {
            id: 'water_ec',
            name: 'УЭП',
            field: 'water_ec',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    withSearch: true,
                    items: getUniqueValues('water_ec'),
                },
            },
        },
        {
            id: 'is_gh',
            name: 'ГХ',
            field: 'is_gh',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    items: [
                        { name: 'Да', value: 'Да' },
                        { name: 'Нет', value: 'Нет' }
                    ],
                },
            },
        },
        {
            id: 'is_icp',
            name: 'ICP',
            field: 'is_icp',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    items: [
                        { name: 'Да', value: 'Да' },
                        { name: 'Нет', value: 'Нет' }
                    ],
                },
            },
        },
        {
            id: 'is_hav',
            name: 'ХАВ',
            field: 'is_hav',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    items: [
                        { name: 'Да', value: 'Да' },
                        { name: 'Нет', value: 'Нет' }
                    ],
                },
            },
        },
        {
            id: 'is_kapel',
            name: 'Капель',
            field: 'is_kapel',
            filterer: (cellValue, filterValues: Array<{ value: string; name: string }>) => {
                return filterValues.some(
                    (filterValue) => filterValue && filterValue.value === cellValue,
                );
            },
            component: {
                name: TableTextFilter,
                props: {
                    items: [
                        { name: 'Да', value: 'Да' },
                        { name: 'Нет', value: 'Нет' }
                    ],
                },
            },
        },
    ];

    //@ts-ignore
    const columnsWells: TableColumn<RowWells>[] = [
        {
        title: '№',
        //@ts-ignore
        accessor: 'rowNumber',
        width: 60,
        renderCell: (row: RowWells, key?: string | number, rowIndex?: number) => {
            // Проверяем, что rowIndex определен и является числом
            if (typeof rowIndex === 'number') {
                const globalIndex = (currentPage - 1) * itemsPerPage + rowIndex + 1;
                return <div>{globalIndex}</div>;
            }
            
            // Если rowIndex не определен, пробуем найти индекс по id
            const index = allRows.findIndex(r => r.id === row.id);
            if (index !== -1) {
                const globalIndex = (currentPage - 1) * itemsPerPage + index + 1;
                return <div>{globalIndex}</div>;
            }
            
            // Если ничего не помогло, показываем прочерк
            return <div>-</div>;
        }
    },
        { title: '№ скважины', accessor: 'number', sortable: true, width: 170 },
        { title: 'Цех', accessor: 'workshop', sortable: true, width: 120 },
        { title: 'Бригада', accessor: 'team', sortable: true, width: 140 },
        { title: 'Название горизонта', accessor: 'horizons_info', sortable: true, width: 170 },
        // { title: 'Шифр горизонта', accessor: 'horizons_info_code', sortable: true, width: 170 },
        { title: 'Пласт', accessor: 'layers_info', sortable: true, width: 200 },
        { title: 'Дебит скажины по жидкости (м³/сут)', accessor: 'liquid_rate', sortable: true, width: 230 },
        { title: 'Обводнённость (%)', accessor: 'water_cut', sortable: true, width: 210 },
        { title: 'Категория', accessor: 'category_name', sortable: true, width: 160 },
        { title: 'Участок', accessor: 'areas', sortable: true, width: 170 },
        { title: 'Q нефти (т/сут)', accessor: 'oil_rate', sortable: true, width: 170 },
        { title: 'Q жидкости (т/сут)', accessor: 'water_rate', sortable: true, width: 180 },
        { title: 'Координаты X', accessor: 'x_coords', sortable: true, width: 170 },
        { title: 'Координаты Y', accessor: 'y_coords', sortable: true, width: 170 },
        { title: 'Исследуемая фаза', accessor: 'studied_phase', sortable: true, width: 190 },
        { title: 'Цель исследования', accessor: 'research_objective', sortable: true, width: 190 },
        { title: 'Примечание', accessor: 'note', sortable: true, width: 170 },
        { title: 'Проба отобрана', accessor: 'is_sample_taken', sortable: true, width: 160 },
        { title: 'Дата отбора пробы', accessor: 'sampling_date', sortable: true, width: 180 },
        { title: 'Примечание пробы', accessor: 'sample_note', sortable: true, width: 160 },
        { title: 'Объем нефти', accessor: 'oil_volume', sortable: true, width: 150 },
        { title: 'Разлито нефть', accessor: 'is_oil_split', sortable: true, width: 150 },
        { title: 'Получен образец воды', accessor: 'is_water_sample_received', sortable: true, width: 170 },
        { title: 'Объем воды', accessor: 'water_volume', sortable: true, width: 150 },
        { title: 'Отфильтровано вода', accessor: 'is_water_split', sortable: true, width: 150 },
        { title: 'Плотность', accessor: 'water_density', sortable: true, width: 160 },
        { title: 'УЭП', accessor: 'water_ec', sortable: true, width: 120 },
        { title: 'ГХ', accessor: 'is_gh', sortable: true, width: 100 },
        { title: 'ICP', accessor: 'is_icp', sortable: true, width: 100 },
        { title: 'ХАВ', accessor: 'is_hav', sortable: true, width: 100},
        { title: 'Капель', accessor: 'is_kapel', sortable: true, width: 140 },
    ];

    const hasActiveFilters = Object.keys(filter || {}).length > 0 || searchParam || 
                            (selectedProject && selectedProject.id !== 'all') || 
                            (selectedField && selectedField.id !== 'all');

    // Очистка всех фильтров
    const clearAllFilters = () => {
        setFilter({});
        setSearchField('');
        setSearchParam('');
        setCurrentPage(1);
        setSelectedProject(projects.find(p => p.id === 'all') || null);
        setSelectedField(fields.find(f => f.id === 'all') || null);
    };

    return (
        <div className={classes.pageContainer}>
            <div className={classes.mainContent}>
                <div className={classes.contentArea}>
                    <div className={classes.contentWrapper}>
                        {/* Шапка страницы */}
                        <div className={classes.pageHeader}>
                            <div className={classes.headerLeft}>
                                <Text size="2xl" weight="bold" className={classes.pageTitle}>
                                    Скважины
                                </Text>
                                {isLoadingWells && <Text size='s' view='secondary'>Загрузка данных...</Text>}
                                {!isLoadingWells && wellsData && (
                                    <Text size='s' view='secondary'>
                                        Показано: {allRows.length} из {wellsData?.count} скважин
                                        {hasActiveFilters && ' (отфильтровано)'}
                                    </Text>
                                )}
                            </div>
                            <div className={classes.headerRight}>
                                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                    <Combobox
                                        items={fields}
                                        value={selectedField}
                                        onChange={setSelectedField}
                                        placeholder="Выберите месторождение"
                                        className={classes.projectCombobox}
                                        style={{ width: '200px' }}
                                    />
                                    <Combobox
                                        items={projects}
                                        value={selectedProject}
                                        onChange={setSelectedProject}
                                        placeholder="Выберите проект"
                                        className={classes.projectCombobox}
                                        style={{ width: '200px' }}
                                    />
                                    {hasActiveFilters && (
                                        <Button
                                            size="s"
                                            view="clear"
                                            label="Очистить фильтры"
                                            onClick={clearAllFilters}
                                        />
                                    )}
                                    <Button
                                        label={isExporting ? "Экспорт..." : "Экспорт в Excel"}
                                        onClick={handleExportToExcel}
                                        size="m"
                                        disabled={isExporting}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Поиск */}
                        <Card verticalSpace="m" horizontalSpace="m" style={{ marginBottom: '20px' }}>
                            <TextField
                                leftSide={IconSearchStroked}
                                placeholder="Поиск по всем полям..."
                                size="s"
                                value={searchField}
                                onChange={(value) => setSearchField(value)}
                                style={{ width: '300px' }}
                                withClearButton
                            />
                        </Card>

                        {/* Таблица скважин */}
                        <Card verticalSpace="l" horizontalSpace="l" className={classes.tableCard}>
                            <div style={{ overflow: 'auto' }}>
                                <Table
                                    columns={columnsWells}
                                    rows={allRows}
                                    verticalAlign="top"
                                    borderBetweenRows
                                    stickyHeader
                                    borderBetweenColumns
                                    stickyColumns={1 && 2}
                                    filters={tableFilters}
                                    onFiltersUpdated={(filters) => updateTable(filters)}
                                    emptyRowsPlaceholder={
                                        <Text>
                                            {isLoadingWells || isFetching ? 'Загрузка данных...' : 'Нет данных для отображения'}
                                        </Text>
                                    }
                                />
                            </div>
                            
                            {/* Пагинация */}
                            {totalPages > 1 && (
                                <div className={classes.paginationSection} style={{ 
                                    marginTop: '20px', 
                                    display: 'flex', 
                                    justifyContent: 'center' 
                                }}>
                                    <Pagination
                                        items={totalPages}
                                        value={currentPage}
                                        onChange={handlePageChange}
                                        showFirstPage
                                        showLastPage
                                        visibleCount={7}
                                        size='s'
                                    />
                                </div>
                            )}
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WellsContent;